import { call, canGetMany } from "./schemaApi"
import { byId, distinctBy, lookup } from "@/services/arrayUtility"
import { getEntity, getQueryField } from "./schemaProvider"
import { getLabelFields } from "./labeller"
import cells from "@/features/schemas/tableCells"
import { isNullOrWhiteSpace } from "@/services/stringUtility"
import { hasProperty } from "@/services/objectUtility"

function getDistinctTypes(foreignKeys) {
    return [...new Set(foreignKeys.map((f) => f.type))]
}

function getDistinctIds(foreignKeys, type) {
    const ids = foreignKeys
        .filter((f) => f.type === type)
        .map((f) => f.ids ?? [])
        .flat()
        .filter((id) => id !== null)

    return [...new Set(ids)]
}

function getForeignKeys(entityKey) {
    const entity = getEntity(entityKey)
    if (!entity) {
        return []
    }
    const fields = entity.queryFields
    const fieldNames = Object.keys(fields)

    return fieldNames
        .map((f) => fields[f])
        .filter((field) => field && "dependsOn" in field && field.dependsOn)
        .map((field) => ({
            key: String(field.key),
            type: field.dependsOn.entityKey,
            baseType: field.type
        }))
}

async function addLookup(type, ids, lookups, scope) {
    const lookupResult = await getLookup(type, ids, scope)
    if (lookupResult != null) {
        lookups[type] = lookupResult
    }
}

async function getLookup(type, ids, scope) {
    if (ids.length) {
        const list = await call(type, "getMany", ids, scope)
        return byId(list.items)
    }
    return null
}

export async function getForeignKeyLookups(entityKey, result, scope) {
    const foreignKeys = getForeignKeys(entityKey).map((field) => ({
        ...field,
        ids:
            field.baseType.includes("list") ?
                result.items.flatMap((item) => item[field.key])
                :   result.items.map((item) => item[field.key])
    }))

    const types = getDistinctTypes(foreignKeys)
        // Don't include any types we cannot list
        .filter((e) => canGetMany(e, scope))

    const lookups = {}
    const promises = []

    for (const type of types) {
        const ids = getDistinctIds(foreignKeys, type)
        promises.push(addLookup(type, ids, lookups, scope))
    }

    // Get all the lookups in parallel
    await Promise.all(promises)
    return lookups
}

export function getLookupTypes(entityKey, scope) {
    const foreignKeys = getForeignKeys(entityKey)
    return (
        getDistinctTypes(foreignKeys)
            // Don't include any types we cannot list
            .filter((e) => canGetMany(e, scope))
    )
}

export function getFields(entityKey, scope, includeDetail = false) {
    const entity = getEntity(entityKey)
    if (!entity) return []

    return Object.values(entity.queryFields).filter(
        (f) =>
            // Hide the id field, unless it's explicitly part of a label
            (f.key !== "id" || "label" in f) &&
            // Hide fields that are annotated with "detailOnly"
            !("detailOnly" in f && !includeDetail) &&
            // Hide fields that are annotated with "nonDisplayable"
            !("nonDisplayable" in f) &&
            // Don't include list columns
            f.type !== "list" &&
            // If the field depends on an entity, make sure we can get the lookup
            (!f.dependsOn || canGetMany(f.dependsOn.entityKey, scope))
    )
}

export function getHeaders(entityKey, scope) {
    const fields = getFields(entityKey, scope).map((f) => ({
        text: f.display ? f.display.name : f.title,
        value: String(f.key)
    }))

    // If there are multiple fields with more than one value, return the first one
    return distinctBy(fields, (f) => f.text)
}

export function getContextHiddenFields(childEntityKey, parentEntityKey, fixedValues, scope) {
    if (!childEntityKey || !parentEntityKey) {
        return []
    }

    const childEntity = getEntity(childEntityKey)
    if (!childEntity?.queryFields) {
        return []
    }

    const hidden = new Set()

    // 1) Always hide fields that directly reference the parent entity.
    Object.values(childEntity.queryFields)
        .filter(field => field?.dependsOn?.entityKey === parentEntityKey)
        .forEach(field => hidden.add(String(field.key)))

    // 1b) Hide fields annotated with hideOnPages when the parent entity is in the list.
    Object.values(childEntity.queryFields)
        .filter(field => field?.hideOnPages?.pages?.includes(parentEntityKey))
        .forEach(field => hidden.add(String(field.key)))

    const fixedKeys = Object.keys(fixedValues ?? {})
    if (!fixedKeys.length) {
        return [...hidden]
    }

    // 2) Also hide label columns that mirror fixed foreign key entities.
    // Example: when a proposal table is scoped to a site, hide "Client" and "Site".
    const titleCandidates = new Set()
    fixedKeys.forEach((fixedKey) => {
        const fixedField = childEntity.queryFields?.[fixedKey]
        const dependsEntityKey = fixedField?.dependsOn?.entityKey
        if (!dependsEntityKey) {
            return
        }
        const dependsEntity = getEntity(dependsEntityKey)
        if (dependsEntity?.singleTitle) {
            titleCandidates.add(dependsEntity.singleTitle.toLowerCase())
        }
        if (dependsEntity?.pluralTitle) {
            titleCandidates.add(dependsEntity.pluralTitle.toLowerCase())
        }
    })

    if (!titleCandidates.size) {
        return [...hidden]
    }

    // Match the field title as shown in the table headers.
    getFields(childEntityKey, scope).forEach((field) => {
        const fieldTitle = field?.display?.name ?? field?.title
        if (!fieldTitle) {
            return
        }
        if (titleCandidates.has(fieldTitle.toString().toLowerCase())) {
            hidden.add(String(field.key))
        }
    })

    return [...hidden]
}

export async function getTableData(entityKey, model, scope, includeLookups = false) {
    const result = await call(entityKey, "list", model, scope)
    result.entityKey = entityKey

    if (includeLookups) {
        result.lookups = await getForeignKeyLookups(entityKey, result, scope)
    }

    result.headers = getHeaders(entityKey, scope)
    return result
}

export function getCellComponent(entityKey, fieldKey) {
    const field = getQueryField(entityKey, fieldKey)
    return field ? cells.getComponent(field) : null
}

export function buildCellOptions(tableData, fieldKey) {
    if (!tableData.entityKey) {
        throw new Error("Entity key is required to build cell options")
    }
    const field = getQueryField(tableData.entityKey, fieldKey)
    if (!field) {
        throw new Error(`Field ${String(fieldKey)} not found for entity ${String(tableData.entityKey)}`)
    }

    const options = {
        field
    }

    if (field.dependsOn) {
        options.entityKey = field.dependsOn.entityKey
        options.lookup = tableData.lookups?.[field.dependsOn.entityKey]
    }

    return options
}

export function applyForeignKeySorting(entityKey, sortBy) {
    // If we're sorting by a foreign key in a table, then we want to sort by the displayed value,
    // and not the Id. To do this, we want to look at the associated label fields and sort by the
    // appropriate path to those fields.

    // If sortBy is a property path (contains '.'), assume the caller knows what they are chasing
    // and do not modify the sorting.
    if (isNullOrWhiteSpace(sortBy) || sortBy.includes(".")) {
        return sortBy
    }

    // Get the relevant label field.
    const field = sortBy ? getQueryField(entityKey, sortBy) : null
    if (!field?.dependsOn?.entityKey) {
        return sortBy
    }

    const labelFields = getLabelFields(field.dependsOn.entityKey)
    if (!labelFields?.length) {
        return sortBy
    }

    // Relying on a convention where the navigation path is named the same as the foreign key,
    // minus the 'Id'. If this proves unreliable, include the navigation path as part of the
    // dependsOn attribute.
    if (sortBy.toLowerCase().endsWith("id")) {
        sortBy = sortBy.substring(0, sortBy.length - 2)
    }

    // TODO: allow for sorting by all label fields, rather than just the first one.
    // This requires a change to the API.
    return `${sortBy}.${labelFields[0].key.toString()}`
}

export async function updateLookups(lookups, messages, scope) {
    const messagesByType = lookup(messages, (m) => m.entityKey)
    const lookupTypes = Object.keys(lookups)

    const promises = []
    for (const type of lookupTypes) {
        if (!hasProperty(messagesByType, type)) {
            continue
        }

        const currentLookup = lookups[type]
        const ids = messagesByType[type]
            .map((m) => m.id)
            .filter((id) => currentLookup[id]) // only refresh the lookup if it's already in the list

        promises.push(updateLookup(type, ids, lookups, scope))
    }

    await Promise.all(promises)
}

async function updateLookup(type, ids, lookups, scope) {
    const updatedLookups = await getLookup(type, ids, scope)
    if (updatedLookups) {
        lookups[type] = {
            ...lookups[type],
            ...updatedLookups
        }
    }
}

export default {
    getHeaders,
    getTableData,
    getCellComponent,
    buildCellOptions,
    updateLookups,
    getContextHiddenFields
}
